/**
 * Outreach connectors on the client (docs/proposals/outreach-connectors.md).
 *
 * Sending tools (Instantly, Benchmark Email) are connected per business by
 * pasting an API key. The key goes to the `outreach-connectors` edge function
 * once and never comes back; `outreach_connections_list()` returns token-free
 * metadata for every business the user belongs to.
 *
 * "Import outreach history" reads a CSV in the browser, maps its columns and
 * sends rows in batches to `import_outreach_history()`, which writes into the
 * active business and is idempotent on (email, campaign, date, status).
 */

import { supabase } from './supabase'
import { parseCsv } from './outreach/csv'

export type ToolProvider = 'instantly' | 'benchmark'
export type ToolStatus = 'connected' | 'syncing' | 'error' | 'needs_reconnect'

export interface ToolConnection {
  id: string
  workspace_id: string
  workspace_name: string
  role: 'owner' | 'admin' | 'member'
  is_current: boolean
  provider: ToolProvider
  label: string | null
  account_label: string | null
  status: ToolStatus
  needs_reconnect: boolean
  last_sync_at: string | null
  last_sync_error: string | null
  connected_by_me: boolean
  created_at: string
  activities: number
  contacts: number
}

export const TOOLS: Record<ToolProvider, { label: string; keyHint: string; what: string }> = {
  instantly: {
    label: 'Instantly',
    what: 'Campaign sends, replies, opens, clicks, bounces.',
    keyHint: 'In Instantly: Settings > Integrations > API Keys > Create. Pick all scopes.',
  },
  benchmark: {
    label: 'Benchmark Email',
    what: 'Campaign sends, opens, clicks, bounces, unsubscribes.',
    keyHint: 'In Benchmark: your name (top right) > Integrations > API Key. Copy the token.',
  },
}

export const TOOL_ORDER: ToolProvider[] = ['instantly', 'benchmark']

const FUNCTIONS_URL = 'https://ouyzishoamkmehmqlnry.supabase.co/functions/v1'

function functionsUrl(): string {
  return (supabase as unknown as { functionsUrl?: string })?.functionsUrl ?? FUNCTIONS_URL
}

function n(v: unknown): number {
  const x = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(x) ? x : 0
}

function asStatus(v: unknown): ToolStatus {
  return v === 'syncing' || v === 'error' || v === 'needs_reconnect' ? v : 'connected'
}

export async function fetchToolConnections(): Promise<ToolConnection[]> {
  const { data, error } = await supabase.rpc('outreach_connections_list')
  if (error) throw new Error(error.message)
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id),
    workspace_id: String(r.workspace_id),
    workspace_name: String(r.workspace_name ?? ''),
    role: (r.role as ToolConnection['role']) ?? 'member',
    is_current: r.is_current === true,
    provider: r.provider === 'benchmark' ? 'benchmark' : 'instantly',
    label: typeof r.label === 'string' ? r.label : null,
    account_label: typeof r.account_label === 'string' ? r.account_label : null,
    status: asStatus(r.status),
    needs_reconnect: r.needs_reconnect === true,
    last_sync_at: typeof r.last_sync_at === 'string' ? r.last_sync_at : null,
    last_sync_error: typeof r.last_sync_error === 'string' ? r.last_sync_error : null,
    connected_by_me: r.connected_by_me === true,
    created_at: String(r.created_at ?? ''),
    activities: n(r.activities),
    contacts: n(r.contacts),
  }))
}

async function callConnectors(body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const { data: sess } = await supabase.auth.getSession()
  const token = sess?.session?.access_token
  if (!token) throw new Error('Sign in first.')
  const res = await fetch(`${functionsUrl()}/outreach-connectors`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok || json.ok === false) {
    throw new Error(
      typeof json.error === 'string' ? json.error : `Request failed (HTTP ${res.status})`,
    )
  }
  return json
}

/** Check and store a key for a business. The first sync starts on the server. */
export async function connectTool(
  provider: ToolProvider,
  apiKey: string,
  workspaceId: string,
): Promise<void> {
  await callConnectors({ action: 'connect', provider, api_key: apiKey, workspace_id: workspaceId })
}

/** Sync one tool now. Returns how many new activities came in. */
export async function syncTool(c: ToolConnection): Promise<number> {
  const json = await callConnectors({
    action: 'sync',
    connection_id: c.id,
    workspace_id: c.workspace_id,
  })
  const results = Array.isArray(json.results)
    ? (json.results as Array<Record<string, unknown>>)
    : []
  const first = results[0]
  if (first && first.ok === false && typeof first.error === 'string') throw new Error(first.error)
  return results.reduce((sum, r) => sum + n(r.activities_added), 0)
}

export async function disconnectTool(c: ToolConnection): Promise<void> {
  await callConnectors({ action: 'disconnect', connection_id: c.id, workspace_id: c.workspace_id })
}

export function toolName(c: Pick<ToolConnection, 'provider' | 'label' | 'account_label'>): string {
  return c.label || c.account_label || TOOLS[c.provider].label
}

// ── Import outreach history ──────────────────────────────────────────────

export type ImportField =
  | 'email'
  | 'name'
  | 'first_name'
  | 'last_name'
  | 'company'
  | 'title'
  | 'campaign'
  | 'at'
  | 'status'
  | 'subject'

export const IMPORT_FIELDS: Array<{ key: ImportField; label: string; required?: boolean }> = [
  { key: 'email', label: 'Email', required: true },
  { key: 'name', label: 'Full name' },
  { key: 'first_name', label: 'First name' },
  { key: 'last_name', label: 'Last name' },
  { key: 'company', label: 'Company' },
  { key: 'title', label: 'Job title' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'at', label: 'Sent date' },
  { key: 'status', label: 'Status' },
  { key: 'subject', label: 'Subject' },
]

/** Header names (normalised: lower case, letters and digits only) each field picks up. */
const SYNONYMS: Record<ImportField, string[]> = {
  email: [
    'email',
    'emailaddress',
    'leademail',
    'recipient',
    'recipientemail',
    'to',
    'toemail',
    'contactemail',
    'mail',
  ],
  name: ['name', 'fullname', 'contactname', 'leadname', 'recipientname'],
  first_name: ['firstname', 'first', 'givenname', 'fname'],
  last_name: ['lastname', 'last', 'surname', 'familyname', 'lname'],
  company: [
    'company',
    'companyname',
    'organization',
    'organisation',
    'account',
    'accountname',
    'business',
  ],
  title: ['title', 'jobtitle', 'position', 'role', 'designation'],
  campaign: ['campaign', 'campaignname', 'sequence', 'sequencename', 'list', 'emailname'],
  at: [
    'sentdate',
    'sentat',
    'date',
    'sent',
    'datesent',
    'senton',
    'timestamp',
    'time',
    'createdat',
    'lastcontacted',
    'lastcontact',
  ],
  status: ['status', 'outcome', 'event', 'result', 'state', 'leadstatus', 'emailstatus'],
  subject: ['subject', 'emailsubject', 'subjectline'],
}

const norm = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '')

export type ColumnMap = Partial<Record<ImportField, number>>

/** Best guess of which column holds each field. A column is used at most once. */
export function autoMatchColumns(headers: string[]): ColumnMap {
  const map: ColumnMap = {}
  const used = new Set<number>()
  const keys = headers.map(norm)
  for (const f of IMPORT_FIELDS.map((x) => x.key)) {
    const idx = SYNONYMS[f].map((s) => keys.indexOf(s)).find((i) => i >= 0 && !used.has(i))
    if (idx !== undefined) {
      map[f] = idx
      used.add(idx)
    }
  }
  if (map.email === undefined) {
    const i = keys.findIndex((k, j) => !used.has(j) && k.includes('email'))
    if (i >= 0) map.email = i
  }
  return map
}

export interface ParsedHistory {
  headers: string[]
  rows: string[][]
}

export function parseHistoryCsv(text: string): ParsedHistory {
  const table = parseCsv(text)
  return { headers: table[0] ?? [], rows: table.slice(1) }
}

export type HistoryRow = Partial<Record<ImportField, string>>

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** A sent date as ISO when the browser can read it, else the raw text. */
export function normaliseDate(raw: string): string {
  const v = raw.trim()
  if (!v) return ''
  const t = Date.parse(v)
  return Number.isNaN(t) ? v : new Date(t).toISOString()
}

/** Turn mapped CSV lines into import rows. Lines without a valid email are counted, not sent. */
export function buildHistoryRows(
  parsed: ParsedHistory,
  map: ColumnMap,
): { rows: HistoryRow[]; skipped: number } {
  const rows: HistoryRow[] = []
  let skipped = 0
  for (const cells of parsed.rows) {
    const get = (f: ImportField) => {
      const i = map[f]
      return i === undefined ? '' : (cells[i] ?? '').trim()
    }
    const email = get('email').toLowerCase()
    if (!EMAIL_RE.test(email)) {
      skipped++
      continue
    }
    const row: HistoryRow = { email }
    for (const f of IMPORT_FIELDS) {
      if (f.key === 'email') continue
      const v = f.key === 'at' ? normaliseDate(get('at')) : get(f.key)
      if (v) row[f.key] = v
    }
    rows.push(row)
  }
  return { rows, skipped }
}

export interface ImportTotals {
  rows: number
  invalid: number
  contacts_created: number
  contacts_matched: number
  activities_added: number
  duplicates: number
}

export const IMPORT_BATCH = 500

/** Send rows to import_outreach_history() in batches; reports progress after each. */
export async function importOutreachHistory(
  rows: HistoryRow[],
  fileName: string,
  onProgress?: (done: number, total: number) => void,
): Promise<ImportTotals> {
  const totals: ImportTotals = {
    rows: 0,
    invalid: 0,
    contacts_created: 0,
    contacts_matched: 0,
    activities_added: 0,
    duplicates: 0,
  }
  for (let i = 0; i < rows.length; i += IMPORT_BATCH) {
    const batch = rows.slice(i, i + IMPORT_BATCH)
    const { data, error } = await supabase.rpc('import_outreach_history', {
      p_rows: batch,
      p_file: fileName,
    })
    if (error) throw new Error(error.message)
    const d = (data ?? {}) as Record<string, unknown>
    for (const k of Object.keys(totals) as Array<keyof ImportTotals>) totals[k] += n(d[k])
    onProgress?.(Math.min(i + IMPORT_BATCH, rows.length), rows.length)
  }
  return totals
}
